import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Paper, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ReactComponent as ActiveCalendarIcon } from 'src/assets/icons/footer/active-calendar-icon.svg';
import { ReactComponent as ActiveCravingIcon } from 'src/assets/icons/footer/active-craving-icon.svg';
import { ReactComponent as ActiveHomeIcon } from 'src/assets/icons/footer/active-home-icon.svg';
import { ReactComponent as ActiveMyPageIcon } from 'src/assets/icons/footer/active-myPage-icon.svg';
import { ReactComponent as ActiveNicojinIcon } from 'src/assets/icons/footer/active-nicojin-icon.svg';
import { ReactComponent as InactiveCalendarIcon } from 'src/assets/icons/footer/inactive-calendar-icon.svg';
import { ReactComponent as InactiveCravingIcon } from 'src/assets/icons/footer/inactive-craving-icon.svg';
import { ReactComponent as InactiveHomeIcon } from 'src/assets/icons/footer/inactive-home-icon.svg';
import { ReactComponent as InactiveMyPageIcon } from 'src/assets/icons/footer/inactive-myPage-icon.svg';
import { ReactComponent as InactiveNicojinIcon } from 'src/assets/icons/footer/inactive-nicojin-icon.svg';
import { FOOTER_HEIGHT, SERVICE_CONTROL_PAGE_PATH } from 'src/assets/static/constants';
import CConfirmModal from 'src/components/custom/modal/CConfirmModal';
import { useHomeMain } from 'src/hooks/queries';
import { useClickTracking } from 'src/hooks/useClickTracking';
import { PATH_PAGE } from 'src/routes/paths';

// 금연 시작 전 접근 차단 대상 경로
const QUIT_REQUIRED_PATHS: string[] = [PATH_PAGE.craving.root, PATH_PAGE.calendar.root];

// -----------------------------------------------------------------

interface BottomNavItem {
  id: string;
  label: string;
  pathname: string;
  activeIcon: ReactNode;
  inactiveIcon: ReactNode;
  activeColor?: string;
}

function Footer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { withTracking } = useClickTracking();
  const { t } = useTranslation();
  const { data: main } = useHomeMain();

  const [value, setValue] = useState<number>(-1);
  const [quitGuardOpen, setQuitGuardOpen] = useState<boolean>(false);

  // 금연 시작 여부: smokeFreeDays > 0 이면 시작 후
  const hasStarted = useMemo(() => {
    return Number(main?.smokeFreeDays) > 0;
  }, [main?.smokeFreeDays]);

  const BottomNavItems = useMemo(
    () => [
      {
        id: SERVICE_CONTROL_PAGE_PATH.home,
        label: t('home.title'),
        pathname: PATH_PAGE.home,
        activeIcon: <ActiveHomeIcon />,
        inactiveIcon: <InactiveHomeIcon />,
      },
      {
        id: SERVICE_CONTROL_PAGE_PATH.drJin,
        label: t('drJin.title'),
        pathname: PATH_PAGE.drJin.root,
        activeIcon: <ActiveNicojinIcon />,
        inactiveIcon: <InactiveNicojinIcon />,
      },
      {
        id: SERVICE_CONTROL_PAGE_PATH.craving,
        label: t('craving.title'),
        pathname: PATH_PAGE.craving.root,
        activeIcon: <ActiveCravingIcon />,
        inactiveIcon: <InactiveCravingIcon />,
        activeColor: '#EF5350',
      },
      {
        id: SERVICE_CONTROL_PAGE_PATH.calendar,
        label: t('calendar.title'),
        pathname: PATH_PAGE.calendar.root,
        activeIcon: <ActiveCalendarIcon />,
        inactiveIcon: <InactiveCalendarIcon />,
      },
      {
        id: SERVICE_CONTROL_PAGE_PATH.myPage,
        label: t('myPage.title'),
        pathname: PATH_PAGE.myPage.root,
        activeIcon: <ActiveMyPageIcon />,
        inactiveIcon: <InactiveMyPageIcon />,
      },
    ],
    [t],
  );

  useEffect(() => {
    const index = BottomNavItems.findIndex((item: BottomNavItem) => item.pathname === pathname);
    setValue(index);
  }, [pathname, BottomNavItems]);

  const handleGuardCancel = () => {
    setQuitGuardOpen(false);
    // 탭 선택을 홈으로 복원하고 홈으로 이동
    setValue(0);
    if (pathname !== PATH_PAGE.home) {
      navigate(PATH_PAGE.home);
    }
  };

  const handleGuardConfirm = () => {
    setQuitGuardOpen(false);
    navigate(PATH_PAGE.calendar.quitSmokingAim);
  };

  return (
    <Container elevation={3}>
      <BottomNavigation
        showLabels
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue);
        }}
        sx={{
          justifyContent: 'space-around',
          height: FOOTER_HEIGHT,
        }}
      >
        {BottomNavItems.map((item: BottomNavItem, index) => {
          return (
            <BottomNavigationAction
              key={index}
              label={item.label}
              value={index}
              onClick={withTracking(item.id, () => {
                // 금연 시작 전에는 닥터진/갈망SOS/일지 접근을 차단하고 안내 팝업 노출
                if (!hasStarted && QUIT_REQUIRED_PATHS.includes(item.pathname)) {
                  setQuitGuardOpen(true);
                  return;
                }
                if (pathname !== item.pathname) {
                  navigate(item.pathname);
                }
              })}
              icon={value === index ? item.activeIcon : item.inactiveIcon}
              sx={
                value === index && item.activeColor
                  ? {
                      '& .MuiBottomNavigationAction-label.Mui-selected': {
                        color: item.activeColor,
                      },
                    }
                  : undefined
              }
            />
          );
        })}
      </BottomNavigation>

      <CConfirmModal
        isOpen={quitGuardOpen}
        title={t('footer.quitGuard.title')}
        content={t('footer.quitGuard.content')}
        cancelText={t('footer.quitGuard.cancel')}
        confirmText={t('footer.quitGuard.confirm')}
        onCancel={handleGuardCancel}
        onConfirm={handleGuardConfirm}
      />
    </Container>
  );
}
export default observer(Footer);

const Container = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  boxShadow: '0px -5px 10px 0px #0000000D',
  '& .MuiBottomNavigationAction-root': {
    minWidth: 0,
    padding: '6px 4px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    '& .MuiBottomNavigationAction-icon': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 30,
    },
  },
  '& .MuiBottomNavigationAction-label': {
    cursor: 'pointer',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: 700,
    color: theme.palette.grey[400],
    marginTop: '4px',
    '&.Mui-selected': {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 700,
      color: theme.palette.primary.main,
    },
  },
}));
